<script setup lang="ts">
const closeIcon = `https://resources1.interface003.com/web30/2026/09/16/a40a522f-ffea-452a-81fe-dd7ee25e91b9.png`
const { t } = useAppI18n()

/** License 认证弹窗数据，来源详情接口 traderTopLabels 中 type=11 的标签描述 */
interface BitLicenseData {
  /** 交易所图标 */
  ico?: string
  /** 弹窗标题 */
  title?: string
  /** 认证说明文案 */
  content?: string
  /** 首次认证时间文案 */
  timeTitle?: string
  /** 认证时间戳，兼容秒级/毫秒级数字与日期字符串 */
  timestamp?: number | string
}

const props = withDefaults(
  defineProps<{
    modelValue: boolean
    bitLicense?: BitLicenseData
    /** 交易所名称，用于「进入 XX 官网」文案 */
    exchangeName?: string
    /** 交易所官网地址，为空时展示纯文案 */
    officialUrl?: string
    isbackWebsite?: boolean
    queryWebsite?: string
  }>(),
  {
    exchangeName: '',
    isbackWebsite: false,
    bitLicense: () => ({}),
    officialUrl: '',
    queryWebsite: '',
  },
)

const emit = defineEmits<{
  'update:modelValue': [value: boolean]
  closed: []
}>()

const dialogVisible = computed({
  get: () => props.modelValue,
  set: (value: boolean) => emit('update:modelValue', value),
})

/**
 * 归一化时间戳为毫秒时间戳
 * @param value 后端返回的认证时间戳，兼容毫秒/秒级数字、日期字符串
 * @returns 毫秒时间戳，无法解析时返回 0
 */
const normalizeTimestamp = (value: unknown): number => {
  if (value === null || value === undefined || value === '') return 0

  // 数字型：10 位按秒处理，13 位按毫秒处理
  if (typeof value === 'number' || /^\d+$/.test(String(value))) {
    const num = Number(value)
    if (!Number.isFinite(num) || num <= 0) return 0

    return num < 1e12 ? num * 1000 : num
  }

  // 字符串型：优先按原样解析（ISO 格式），失败再兼容 iOS 不友好的 "YYYY-MM-DD HH:mm:ss"
  const raw = String(value)
  const directParsed = Date.parse(raw)
  if (Number.isFinite(directParsed)) return directParsed

  const fallbackParsed = new Date(raw.replace(/-/g, '/')).getTime()

  return Number.isFinite(fallbackParsed) ? fallbackParsed : 0
}

/**
 * 认证时长（天）：认证当天算第 1 天，之后每跨过一个 0 点增加 1 天
 * @returns 实际天数，最小为 1
 */
const durationDays = computed(() => {
  const startTime = normalizeTimestamp(props.bitLicense?.timestamp)
  if (!startTime) return 1

  const startDate = new Date(startTime)
  const currentDate = new Date()
  // 使用本地日期计算自然日差，再转为 UTC 零点，避免夏令时影响天数结果。
  const startDay = Date.UTC(startDate.getFullYear(), startDate.getMonth(), startDate.getDate())
  const currentDay = Date.UTC(currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate())
  const diffDays = Math.floor((currentDay - startDay) / (1000 * 60 * 60 * 24))

  return Math.max(diffDays + 1, 1)
})

/**
 * 时长的逐位展示数组：按实际位数展示（如 1 → ['1']、199 → ['1','9','9']），不设最大位数
 * @returns 每位数字字符组成的数组，模板按位数渲染等宽数字格
 */
const durationDigits = computed(() => String(durationDays.value).split(''))

const closeDialog = () => {
  dialogVisible.value = false
}

const handleOfficialClick = () => {
  if (props.officialUrl) return
  closeDialog()
}
</script>

<template>
  <ElDialog
    v-model="dialogVisible"
    width="540px"
    align-center
    append-to-body
    destroy-on-close
    :show-close="false"
    :close-on-click-modal="false"
    modal-class="!z-[5100]"
    style="font-family: 'PingFang SC'"
    class="!h-fit !overflow-hidden !rounded-[28px] !p-0"
    header-class="!hidden"
    body-class="!p-0"
    @closed="emit('closed')"
  >
    <div class="overflow-hidden rounded-[28px] bg-white font-[Outfit] text-[#292a2e]">
      <div class="px-[32px] pt-[32px]">
        <div class="flex items-center gap-[8px] border-b-[0.5px] border-solid border-[rgba(5,21,36,0.06)] pb-[16px]">
          <img :src="props.bitLicense?.ico" alt="" class="size-[32px] shrink-0" />
          <h2 class="min-w-0 flex-1 truncate text-[20px] leading-[28px] font-[600]">{{ props.bitLicense?.title }}</h2>

          <CommonImage
            class="flex size-[32px] shrink-0 cursor-pointer"
            src="https://resources1.interface003.com/web30/2026/09/24/61d39f28-e07f-448a-8341-3958b2bfff48.png"
            alt=""
            @click="closeDialog"
          />
        </div>
      </div>

      <div class="flex flex-col items-center gap-[20px] px-[32px] pt-[20px] pb-[32px]">
        <div class="flex w-full flex-col items-start gap-[16px]">
          <i18n-t keypath="89959" tag="div" class="flex items-end gap-[8px]">
            <div dir="ltr" class="flex items-center gap-[6px]">
              <div
                v-for="(digit, index) in durationDigits"
                :key="`${digit}-${index}`"
                class="flex size-[40px] items-center justify-center rounded-[4px] border-[0.5px] border-solid border-[rgba(5,21,36,0.06)] bg-[rgba(24,104,219,0.06)] text-[32px] leading-none font-[600] text-[#112d73]"
              >
                {{ digit }}
              </div>
            </div>
          </i18n-t>

          <div class="flex w-full flex-col items-start gap-[8px]">
            <div class="w-full rounded-[8px] bg-[#f8f8f8] px-[16px] py-[12px] text-[14px] leading-[22px]">
              {{ props.bitLicense?.content }}
            </div>
            <p dir="ltr" class="w-full text-[14px] leading-[22px] text-[#505258]">{{ props.bitLicense?.timeTitle }}</p>
          </div>
        </div>

        <div class="flex w-full flex-col items-center gap-[8px]">
          <button
            type="button"
            class="blue-hover h-[46px] min-w-[184px] cursor-pointer rounded-full bg-[#112d73] px-[64px] py-[12px] text-center text-[14px] leading-[22px] font-[600] text-[#fff]"
            @click="closeDialog"
          >
            {{ t('83585') }}
          </button>
          <a
            v-if="props.officialUrl && !props.isbackWebsite"
            :href="props.officialUrl"
            target="_blank"
            rel="noopener noreferrer"
            class="text-center text-[13px] leading-[20px] text-[#7d818a]"
          >
            {{ t('89958', [props.exchangeName]) }}
          </a>
          <a
            v-else-if="props.isbackWebsite && props.queryWebsite"
            :href="props.queryWebsite"
            target="_blank"
            rel="noopener noreferrer"
            class="text-center text-[13px] leading-[20px] text-[#7d818a]"
          >
            {{ t('89957', [props.exchangeName]) }}
          </a>
        </div>
      </div>
    </div>
  </ElDialog>
</template>
